import { LanCode } from "@/store/reducers/setting";

export const SEO_CONSTANTS = {
  BASE_URL: "https://www.bhaptics.com",
  // 페이지가 자기 seo.ogImage 명시한 곳 (제품 상세, 뉴스 등) 은 자동으로 그대로 유지.
  DEFAULT_OG_IMAGE: "/images/opengraph-image.jpg",
  SITE_NAME: "bHaptics",
  TWITTER_HANDLE: "@bhaptics",
} as const;

/**
 * 지원 언어 목록
 */
export const SUPPORTED_LOCALES: LanCode[] = [
  LanCode.English,
  LanCode.Korean,
  LanCode.Chinese,
  LanCode.Japanese,
  LanCode.German,
  LanCode.French,
  LanCode.Spanish,
];

/**
 * 언어 코드를 ISO 639-1 hreflang 형식으로 변환
 * (kr → ko 등 표준화)
 */
/**
 * LanCode → **URL 의 로케일 세그먼트**(next-i18next `locales`). LanCode 값(`kr` · `jp` · `cn`)은 URL 이 아니다 —
 * `/kr/…` 는 `/ko/…` 로 리다이렉트된다(`next.config.mjs` — 2026-10-30 부터 308, 그 전엔 307).
 * hreflang · canonical 은 리다이렉트되는 주소를 가리키면 안 된다
 * (2026-10-29 prod 실측: hreflang 이 `/kr/` · `/jp/` · `/cn/` 이었다).
 */
export const LOCALE_TO_URL_SEGMENT: Record<LanCode, string> = {
  [LanCode.English]: "en",
  [LanCode.Korean]: "ko",
  [LanCode.Chinese]: "zh",
  [LanCode.Japanese]: "ja",
  [LanCode.German]: "de",
  [LanCode.French]: "fr",
  [LanCode.Spanish]: "es",
};

export const LOCALE_TO_HREFLANG: Record<LanCode, string> = {
  [LanCode.English]: "en",
  [LanCode.Korean]: "ko",
  [LanCode.Chinese]: "zh-Hans",
  [LanCode.Japanese]: "ja",
  [LanCode.German]: "de",
  [LanCode.French]: "fr",
  [LanCode.Spanish]: "es",
};

/**
 * 기본 언어 (x-default)
 */
export const DEFAULT_LOCALE = LanCode.English;

/**
 * BreadcrumbList JSON-LD 생성
 *
 * ## ★경로부에 끝슬래시를 붙인다 — 여기는 `<Link>` 가 아니다 (#5795)
 *
 * 이 리포는 `trailingSlash: true`(`next.config.mjs`)라 슬래시 없는 경로는 **308 을 한 번
 * 더** 쓴다. 화면 링크는 그 홉이 안 보인다 — `next/link` 가 경로부 끝슬래시와 로케일
 * 접두를 **둘 다 정규화**하기 때문이다(prod 실측 2026-09-17: 소스 `href: "/games/pc"` →
 * 앵커 `/ko/games/pc/`). 그래서 `href` 쪽은 **표기가 홉을 바꾸지 않는다** — 실제로 리포에
 * 두 표기가 섞여 있다(표기가 갈린 경로 10 / 고유 52 · 같은 날 실측).
 *
 * ★**브레드크럼은 그 정규화를 못 받는다.** 여기서 만든 문자열은 JSON-LD 안에 **절대 URL
 * 로 그대로 박혀** 크롤러가 그 값을 따라간다. 그래서 같은 표기를 쓰면 **모든 항목이 308**
 * 이다. prod 실측(2026-09-17 · 두 생성기 6경로 전부):
 *
 * ```
 * /ko                                            홉 1   ← 슬래시 누락
 * /ko/tactsuit                                   홉 1
 * /ko/shop/buy-tactsuit/tactsuit-pro             홉 1
 * /ko/games/experiences/half-life-alyx           홉 1
 * /ko/games                                      홉 2   ← 슬래시 + 스텁(#5683). 목적지를 함께 고쳤다
 * ```
 *
 * ⚠️ `path: ""`(Home)도 대상이다 — `${BASE_URL}/${locale}` 는 `/ko` 라 똑같이 308 이다.
 *
 * ## ★방어 분기를 두지 않는다 — 전제가 오늘 0건이다 (#5795 R1 F2)
 *
 * 초판은 `item.path.replace(/\/+$/, "")` 로 *「호출처가 끝슬래시를 이미 적어도 `//` 가 안
 * 되게」* 라는 분기를 뒀다. **그 분기는 한 번도 안 돈다** — 호출처 2곳
 * (`game-seo.ts:165` · `product-seo.ts:104`)의 6경로가 전부 무슬래시다. 그래서 지워도
 * 가드가 안 빨개졌다(실측 2026-09-17 · `mutate.sh` · 치환 1건 → `16 passed` 미검출).
 *
 * 전달하는 호출처가 0곳인 방어는 **검증할 수 없는 코드**다. 그래서 분기 대신 **단언**으로
 * 옮겼다 — `redirect-hops.test.ts` 의 브레드크럼 케이스가 *「방출된 `item` 의 경로부는
 * 정확히 하나의 슬래시로 끝난다」* 를 본다(`//` 도 거절). 호출처가 `/a/` 를 넘기기 시작하면
 * 그때 빨개지고, 그 자리에서 분기를 만들지 값을 고칠지 판단하게 된다.
 *
 * ⚠️ 쿼리가 붙은 `path` 도 같은 이유로 분기를 안 만든다 — 지금 0건이다. 생기면
 *    `?a=b/` 가 되어 위 단언이 잡는다.
 */
export function buildBreadcrumb(
  locale: string,
  items: { name: string; path: string }[],
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${SEO_CONSTANTS.BASE_URL}/${locale}${item.path}/`,
    })),
  };
}
